import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

// ─── Palette cohérente avec LandingEntreprise — réhaussée ───────────────────
const C = {
  bg:      '#020617',
  orange:  '#f97316',
  amber:   '#f59e0b',
  red:     '#ef4444',
  emerald: '#10b981',
  cyan:    '#22d3ee',
  // ↑ opacités augmentées pour la lisibilité
  muted:   'rgba(255, 255, 255, 0.94)',
  dim:     'rgba(255, 255, 255, 0.78)',
  faint:   'rgba(255, 255, 255, 0.5)',
};

// ─── Données dettes (inchangées) ─────────────────────────────────────────────
const DETTES = [
  { type: 'DETTE.COGNITIVE',    label: 'Réunions sans décision · Boucles infinies · Surcharge mentale', ratePerSec: 1340, color: C.orange,  peak: 187000 },
  { type: 'DETTE.ÉMOTIONNELLE', label: 'Épuisement silencieux · Désengagement · Perte de sens',        ratePerSec: 890,  color: '#fb923c', peak: 124000 },
  { type: 'DETTE.SOCIALE',      label: "Silos qui se renforcent · Confiance qui s'érode · Ruptures",   ratePerSec: 620,  color: C.amber,   peak: 86000 },
  { type: 'DETTE.DÉCISIONNELLE',label: 'Décisions bloquées · Responsabilités floues · Paralysie',       ratePerSec: 1100, color: '#fbbf24', peak: 153000 },
  { type: 'DETTE.FINANCIÈRE',   label: 'Coût systémique annuel · Estimation conservative',              ratePerSec: 3200, color: C.red,     peak: 520000 },
];

const NODES_RAW = [
  { id: 'root', x: 0.5,  y: 0.42, label: 'Décision\ncritique', type: 'root' },
  { id: 'b1',   x: 0.22, y: 0.20, label: 'Réunion\ncadrage',   type: 'blocked' },
  { id: 'b2',   x: 0.78, y: 0.20, label: 'Validation\nDG',     type: 'blocked' },
  { id: 'b3',   x: 0.14, y: 0.58, label: 'Consensus\nRH',      type: 'loop' },
  { id: 'b4',   x: 0.86, y: 0.58, label: 'Budget\ngel',        type: 'blocked' },
  { id: 'b5',   x: 0.38, y: 0.76, label: 'Arbitrage\nsilos',   type: 'loop' },
  { id: 'b6',   x: 0.62, y: 0.76, label: 'Escalade\nhiérarch.', type: 'blocked' },
  { id: 'l1',   x: 0.06, y: 0.38, label: 'Relance ×4',         type: 'micro' },
  { id: 'l2',   x: 0.28, y: 0.90, label: 'Report ×6',          type: 'micro' },
  { id: 'l3',   x: 0.72, y: 0.90, label: 'CR ×3',              type: 'micro' },
];

const EDGES = [
  ['root','b1'],['root','b2'],['root','b3'],['root','b4'],['root','b5'],['root','b6'],
  ['b3','l1'],['b5','l2'],['b6','l3'],
  ['l1','b3'],['l2','b5'],['l3','b6'],
];

const TRACES = [
  { label: 'Bifurcations documentées', val: '847',  color: C.cyan,    delay: 0.2 },
  { label: 'Trajectoires conservées',  val: '312',  color: C.emerald, delay: 0.4 },
  { label: 'Blocages résolus',         val: '−40%', color: C.emerald, delay: 0.6 },
  { label: 'Collaboration transv.',    val: '+65%', color: C.amber,   delay: 0.8 },
  { label: 'Abnégation renforcée',     val: '×2.8', color: C.orange,  delay: 1.0 },
];

// ═══════════════════════════════════════════════════════════════════════════
// SCÈNE 0 — Le coût qui s'accumule
// ═══════════════════════════════════════════════════════════════════════════
function SceneCout({ active }) {
  const [values, setValues] = useState(DETTES.map(() => 0));
  const [total, setTotal]   = useState(0);
  const rafRef   = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    if (!active) {
      cancelAnimationFrame(rafRef.current);
      setValues(DETTES.map(() => 0));
      setTotal(0);
      return;
    }
    startRef.current = performance.now();
    const tick = (now) => {
      const elapsed = (now - startRef.current) / 1000;
      const newVals = DETTES.map(d => Math.min(d.peak, Math.floor(d.ratePerSec * elapsed)));
      setValues(newVals);
      setTotal(newVals.reduce((a, b) => a + b, 0));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [active]);

  return (
    <div className="absolute inset-0 flex flex-col justify-center px-6 sm:px-12 py-10">

      {/* Label — opacité 0.6 → 0.95 */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: active ? 1 : 0 }}
        className="absolute top-5 left-6 flex items-center gap-2"
      >
        <motion.div className="w-1.5 h-1.5 rounded-full" style={{ background: C.red }}
          animate={{ opacity: active ? [1, 0.2, 1] : 0 }}
          transition={{ duration: 1, repeat: Infinity }} />
        <span className="text-[9px] font-mono uppercase tracking-[0.3em]"
          style={{ color: 'rgba(252, 165, 165, 0.95)' }}>  {/* ← plus lumineux */}
          Coût invisible · accumulation en cours
        </span>
      </motion.div>

      {/* Lignes de dette */}
      <div className="space-y-3 sm:space-y-4 max-w-2xl mx-auto w-full">
        {DETTES.map((d, i) => {
          const pct = (values[i] / d.peak) * 100;
          return (
            <motion.div key={d.type}
              initial={{ opacity: 0, x: -14 }}
              animate={{ opacity: active ? 1 : 0, x: active ? 0 : -14 }}
              transition={{ delay: i * 0.18, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="flex items-baseline justify-between mb-1 gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[10px] sm:text-[11px] font-mono font-bold shrink-0"
                    style={{ color: d.color }}>
                    {d.type}
                  </span>
                  {/* label descriptif — dim remonté via C.dim = 0.78 */}
                  <span className="text-[8px] font-mono truncate hidden sm:block"
                    style={{ color: C.dim }}>
                    {d.label}
                  </span>
                </div>
                <span className="text-[11px] sm:text-sm font-mono font-black shrink-0 tabular-nums"
                  style={{ color: d.color }}>
                  €{values[i].toLocaleString('fr-FR')}
                </span>
              </div>
              {/* rail plus visible 0.05 → 0.12 */}
              <div className="h-px w-full" style={{ background: 'rgba(255,255,255,0.12)' }}>
                <motion.div className="h-full" style={{ background: d.color, opacity: 0.85 }}
                  animate={{ width: `${pct}%` }} transition={{ duration: 0.1 }} />
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Total */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: active ? 1 : 0 }}
        transition={{ delay: 0.9 }}
        className="max-w-2xl mx-auto w-full mt-5 pt-4"
        style={{ borderTop: '1px solid rgba(249,115,22,0.28)' }}   // ← 0.12 → 0.28
      >
        <div className="flex items-baseline justify-between">
          <span className="text-[9px] font-mono uppercase tracking-[0.3em]"
            style={{ color: 'rgba(253, 186, 116, 0.95)' }}>       {/* ← 0.5 → 0.95 */}
            Coût systémique annuel estimé
          </span>
          <span className="text-xl sm:text-3xl font-black font-mono tabular-nums"
            style={{
              background: 'linear-gradient(90deg, #fb923c, #fcd34d)',  // ← plus lumineux
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
            €{total.toLocaleString('fr-FR')}
          </span>
        </div>
        <p className="text-[8px] font-mono italic mt-1"
          style={{ color: 'rgba(255,255,255,0.45)' }}>            {/* ← 0.15 → 0.45 */}
          Projection · Cohortes pilotes 2011–2025 · Les résultats varient selon le contexte organisationnel
        </p>
      </motion.div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SCÈNE 1 — La bifurcation bloquée
// ═══════════════════════════════════════════════════════════════════════════
function SceneBifurcation({ active }) {
  const containerRef = useRef(null);
  const [dims, setDims]       = useState({ w: 600, h: 340 });
  const [revealed, setRevealed] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      setDims({ w: width, h: height });
    });
    obs.observe(containerRef.current);
    const { width, height } = containerRef.current.getBoundingClientRect();
    setDims({ w: width, h: height });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!active) { setRevealed(0); return; }
    let i = 0;
    const iv = setInterval(() => {
      i++;
      setRevealed(i);
      if (i >= NODES_RAW.length) clearInterval(iv);
    }, 220);
    return () => clearInterval(iv);
  }, [active]);

  const nodes = NODES_RAW.map(n => ({ ...n, px: n.x * dims.w, py: n.y * dims.h }));
  const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]));

  const typeColor  = { root: C.orange, blocked: C.red, loop: C.amber, micro: 'rgba(251,191,36,0.6)' }; // ← micro +visible
  const typeRadius = { root: 10, blocked: 6, loop: 6, micro: 3 };

  return (
    <div ref={containerRef} className="absolute inset-0">

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: active ? 1 : 0 }}
        className="absolute top-5 left-6 flex items-center gap-2 z-10">
        <span className="text-[9px] font-mono uppercase tracking-[0.3em]"
          style={{ color: 'rgba(253, 186, 116, 0.95)' }}>       {/* ← 0.5 → 0.95 */}
          Bifurcation · décision bloquée
        </span>
      </motion.div>

      {/* Légende — labels plus lisibles */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: active ? 1 : 0 }}
        transition={{ delay: 1 }}
        className="absolute top-5 right-6 flex flex-col gap-1 z-10">
        {[{ color: C.red, label: 'Bloqué' }, { color: C.amber, label: 'Boucle' }].map(l => (
          <div key={l.label} className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ background: l.color }} />
            <span className="text-[8px] font-mono" style={{ color: C.muted }}>{l.label}</span>
          </div>
        ))}
      </motion.div>

      <svg className="absolute inset-0 w-full h-full">
        <defs>
          <marker id="arr-blocked" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 Z" fill={C.red} opacity="0.75" />
          </marker>
          <marker id="arr-loop" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 Z" fill={C.amber} opacity="0.75" />
          </marker>
        </defs>

        {EDGES.map(([from, to], i) => {
          const a = nodeMap[from];
          const b = nodeMap[to];
          if (!a || !b) return null;
          const fromIdx = NODES_RAW.findIndex(n => n.id === from);
          const toIdx   = NODES_RAW.findIndex(n => n.id === to);
          const vis     = Math.min(fromIdx, toIdx) < revealed;
          const isLoop  = b.type === 'loop' || a.type === 'micro';
          return (
            <line key={i}
              x1={a.px} y1={a.py} x2={b.px} y2={b.py}
              stroke={isLoop ? C.amber : C.red}
              strokeWidth={1.2}
              strokeOpacity={vis ? 0.55 : 0}                 // ← 0.3 → 0.55
              strokeDasharray={isLoop ? '3 5' : undefined}
              markerEnd={`url(#${isLoop ? 'arr-loop' : 'arr-blocked'})`}
              style={{ transition: 'stroke-opacity 0.4s' }}
            />
          );
        })}

        {nodes.map((n, i) => {
          const r     = typeRadius[n.type];
          const color = typeColor[n.type];
          const vis   = i < revealed;
          return (
            <g key={n.id} opacity={vis ? 1 : 0} style={{ transition: 'opacity 0.3s' }}>
              {n.type !== 'micro' && (
                <circle cx={n.px} cy={n.py} r={r + 7} fill={color} opacity={0.1} /> 
              )}
              <circle cx={n.px} cy={n.py} r={r}
                fill={C.bg} stroke={color} strokeWidth={n.type === 'root' ? 2 : 1.3} />
              {n.type === 'blocked' && (
                <line x1={n.px - 3} y1={n.py} x2={n.px + 3} y2={n.py}
                  stroke={C.red} strokeWidth={1.5} />
              )}
              {n.type !== 'micro' && (
                <text x={n.px} y={n.py + r + 11} textAnchor="middle"
                  fontSize={dims.w < 400 ? 7 : 8} fontFamily="monospace"
                  fill={color} opacity={0.92}>                   {/* ← 0.65 → 0.92 */}
                  {n.label.split('\n').map((line, li) => (
                    <tspan key={li} x={n.px} dy={li === 0 ? 0 : 10}>{line}</tspan>
                  ))}
                </text>
              )}
              {n.type === 'root' && vis && (
                <circle cx={n.px} cy={n.py} r={r + 4} fill="none"
                  stroke={C.orange} strokeWidth={1} opacity={0.35}>  {/* ← 0.25 → 0.35 */}
                  <animate attributeName="r" values={`${r+4};${r+20};${r+4}`} dur="2.5s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.35;0;0.35" dur="2.5s" repeatCount="indefinite" />
                </circle>
              )}
            </g>
          );
        })}
      </svg>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: active && revealed >= NODES_RAW.length ? 1 : 0 }}
        transition={{ delay: 0.5 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center"
      >
        <div className="text-[9px] font-mono uppercase tracking-[0.25em] mb-1"
          style={{ color: 'rgba(252, 165, 165, 0.95)' }}>          {/* ← 0.5 → 0.95 */}
          Décisions en attente de résolution
        </div>
        <div className="text-2xl sm:text-3xl font-black font-mono" style={{ color: C.red }}>43%</div>
        <div className="text-[8px] font-mono italic mt-0.5" style={{ color: C.dim }}>
          Cohortes pilotes 2011–2025
        </div>
      </motion.div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SCÈNE 2 — La trace (clarté documentée)
// ═══════════════════════════════════════════════════════════════════════════
function SceneTrace({ active }) {
  return (
    <div className="absolute inset-0 flex flex-col justify-center px-6 sm:px-12 py-10">

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: active ? 1 : 0 }}
        className="absolute top-5 left-6 flex items-center gap-2">
        <motion.div className="w-1.5 h-1.5 rounded-full" style={{ background: C.emerald }}
          animate={{ opacity: active ? [1, 0.2, 1] : 0 }}
          transition={{ duration: 1.6, repeat: Infinity }} />
        <span className="text-[9px] font-mono uppercase tracking-[0.3em]"
          style={{ color: 'rgba(52, 211, 153, 0.95)' }}>         {/* ← 0.6 → 0.95 */}
          PESACORE · Traversée documentée
        </span>
      </motion.div>

      <div className="max-w-2xl mx-auto w-full">
        {/* Phrase clé — remontée au blanc quasi pur */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: active ? 1 : 0, y: active ? 0 : 10 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mb-8"
        >
          <p className="font-black uppercase tracking-tight leading-[1.1]"
            style={{
              fontSize: 'clamp(1.05rem, 3vw, 1.55rem)',
              fontFamily: 'monospace',
              color: 'rgba(255,255,255,0.98)',             // ← 0.85 → 0.98
              textShadow: '0 0 24px rgba(249,115,22,0.15)', // ← halo léger
            }}>
            La complexité n'est pas
          </p>
          <p style={{
            fontSize: 'clamp(1.05rem, 3vw, 1.55rem)',
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
            fontWeight: 900,
            background: 'linear-gradient(135deg, #fb923c, #fcd34d)', // ← plus lumineux
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            filter: 'drop-shadow(0 0 20px rgba(249,115,22,0.25))',   // ← halo
          }}>
            une fatalité.
          </p>
        </motion.div>

        {/* Métriques — labels passés de 0.22 → 0.55 */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-px"
          style={{ background: 'rgba(249,115,22,0.12)' }}>
          {TRACES.map((t) => (
            <motion.div key={t.label}
              initial={{ opacity: 0 }}
              animate={{ opacity: active ? 1 : 0 }}
              transition={{ delay: t.delay, duration: 0.5 }}
              className="p-3 sm:p-4 relative"
              style={{ background: C.bg }}
            >
              <div className="text-[8px] font-mono uppercase tracking-widest mb-1"
                style={{ color: 'rgba(255,255,255,0.55)' }}>       {/* ← 0.22 → 0.55 */}
                {t.label}
              </div>
              <div className="text-xl sm:text-2xl font-black font-mono leading-none"
                style={{ color: t.color, textShadow: `0 0 16px ${t.color}40` }}>  {/* ← halo coloré */}
                {t.val}
              </div>
              <motion.div className="absolute bottom-0 left-0 h-px"
                style={{ background: t.color, opacity: 0.5 }}          // ← 0.25 → 0.5
                initial={{ width: 0 }}
                animate={{ width: active ? '100%' : 0 }}
                transition={{ delay: t.delay + 0.2, duration: 0.6 }}
              />
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: active ? 1 : 0 }}
          transition={{ delay: 1.4 }}
          className="text-[8px] font-mono italic mt-3"
          style={{ color: 'rgba(255,255,255,0.45)' }}>               {/* ← 0.15 → 0.45 */}
          Observations issues de cohortes réelles · Cohortes pilotes 2011–2025 · Les résultats varient
        </motion.p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ROOT
// ═══════════════════════════════════════════════════════════════════════════
const SCENE_DURATIONS = [9000, 8000, 8000];

export default function GrandeEcoleSpot() {
  const [scene, setScene] = useState(0);
  const hasPlayedRef = useRef(false);
  const timerRef = useRef(null);
  const audioRef     = useRef(null);

  const scheduleNext = (current) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const next = (current + 1) % SCENE_DURATIONS.length;
      setScene(next);
      scheduleNext(next);
    }, SCENE_DURATIONS[current]);
  };

  useEffect(() => {
    const seq = [
      { time: 14000, scene: 1 },
      { time: 22000, scene: 2 },
      { time: 29000, scene: 3 },
    ];
    const timers = seq.map(({ time, scene }) => setTimeout(() => setScene(scene), time));
    return () => timers.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || hasPlayedRef.current) return;
    hasPlayedRef.current = true;
    audio.volume = 0.4;
    audio.play().catch(() => {});
  }, []);

  const goTo = (i) => {
    clearTimeout(timerRef.current);
    setScene(i);
    scheduleNext(i);
  };

  return (
    <div className="relative w-full h-full overflow-hidden"
      style={{ background: C.bg, fontFamily: 'monospace' }}>
      <audio ref={audioRef}>
        <source src="/audio/landing-spot.mp3" />
      </audio>

      {/* Grille — opacité légèrement relevée 0.025 → 0.045 */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: `
          linear-gradient(rgba(249,115,22,0.045) 1px, transparent 1px),
          linear-gradient(90deg, rgba(249,115,22,0.045) 1px, transparent 1px)
        `,
        backgroundSize: '32px 32px',
      }} />

      {/* Halo coloré bas-gauche (identité) — un peu plus présent */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 20% 30%, rgba(249,115,22,0.09), transparent 65%)' }} />

      {/* ⭐ NOUVELLE source lumineuse : halo blanc doux en haut centre */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(255,255,255,0.06), transparent 55%)' }} />

      <AnimatePresence mode="wait">
        {scene === 0 && (
          <motion.div key="cout" className="absolute inset-0"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}>
            <SceneCout active />
          </motion.div>
        )}
        {scene === 1 && (
          <motion.div key="bifurcation" className="absolute inset-0"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}>
            <SceneBifurcation active />
          </motion.div>
        )}
        {scene === 2 && (
          <motion.div key="trace" className="absolute inset-0"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}>
            <SceneTrace active />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Timeline — inactifs légèrement remontés */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 z-50">
        {['01', '02', '03'].map((label, i) => (
          <button key={i} onClick={() => goTo(i)}
            className="flex items-center gap-1.5">
            <div className="transition-all duration-300 rounded-full" style={{
              width:      scene === i ? '24px' : '6px',
              height:     '2px',
              background: scene === i
                ? 'linear-gradient(90deg, #fb923c, #fcd34d)'
                : 'rgba(251,146,60,0.35)',                  // ← 0.2 → 0.35
            }} />
            <span className="text-[7px] font-mono uppercase tracking-widest transition-all" style={{
              color: scene === i ? 'rgba(253,186,116,0.95)' : 'rgba(255,255,255,0.9)',  // ← 0.6/0.8 → 0.95/0.9
            }}>
              {label}
            </span>
          </button>
        ))}
      </div>

      {/* Watermark */}
      <div className="absolute bottom-4 right-5 text-[7px] font-mono uppercase tracking-widest"
        style={{ color: 'rgba(255,255,255,0.88)' }}>          {/* ← 0.8 → 0.88 */}
        PESACORE · 2011–2025
      </div>
    </div>
  );
}